import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatNumber } from "../utils/format";
import ErrorModal from "../components/ErrorModal";

const STEPS = {
  SETUP: "setup",
  SCANNING: "scanning",
  PRODUCT: "product",
  LOCATION_SCAN: "location_scan",
  FINISH: "finish",
};

// Dutch month names for default stock count name
const MONTHS = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

export default function StockCountScan({ user }) {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const processingRef = useRef(false); // Guard against multiple simultaneous scan callbacks

  // State machine
  const [step, setStep] = useState(STEPS.SETUP);

  // Setup form
  const now = new Date();
  const [formData, setFormData] = useState({
    name: `Telling ${MONTHS[now.getMonth()]} ${now.getFullYear()}`,
    date: now.toISOString().split("T")[0],
  });

  // Scanned items
  const [items, setItems] = useState([]);

  // Current product being processed
  const [scanInfo, setScanInfo] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [quantity, setQuantity] = useState(0);

  // Location scan context (for step 4)
  const [pendingProductId, setPendingProductId] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [scannerActive, setScannerActive] = useState(false);

  // Screen size check
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Start QR scanner (or resume if already paused)
  const startScanner = useCallback(async () => {
    processingRef.current = false;

    // If scanner already exists, just resume it
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 3) { // PAUSED
          html5QrCodeRef.current.resume();
          setScannerActive(true);
          return;
        }
        if (state === 2) { // Already SCANNING
          setScannerActive(true);
          return;
        }
      } catch (err) {
        // State check failed, fall through to create new scanner
      }
    }

    if (!scannerRef.current) return;

    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          // Guard: ignore if already processing a scan
          if (processingRef.current) return;
          processingRef.current = true;

          // Vibrate on scan
          if (navigator.vibrate) navigator.vibrate(100);

          // Pause scanner (keeps camera running, stops decoding) — safe to call from callback
          try { scanner.pause(true); } catch (e) { /* ignore */ }
          setScannerActive(false);

          // Store the result for processing via useEffect
          setPendingScan(decodedText);
        },
        () => {} // ignore scan failures
      );
      setScannerActive(true);
    } catch (err) {
      console.error("Scanner start failed:", err);
      setErrorModal({
        show: true,
        message: "Kon de camera niet starten. Controleer de camera-instellingen.",
      });
    }
  }, []);

  // Stop QR scanner — fully stops camera (only used on unmount/navigation)
  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2 || state === 3) {
          await html5QrCodeRef.current.stop();
        }
      } catch (err) {
        // ignore stop errors
      }
      html5QrCodeRef.current = null;
      setScannerActive(false);
    }
  }, []);

  // Pause scanner (keeps camera alive, just stops decoding)
  const pauseScanner = useCallback(() => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING
          html5QrCodeRef.current.pause(true);
        }
      } catch (err) {
        // ignore
      }
      setScannerActive(false);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        try {
          html5QrCodeRef.current.stop().catch(() => {});
        } catch (e) {
          // ignore
        }
        html5QrCodeRef.current = null;
      }
    };
  }, []);

  // Pending scan result — processed via useEffect to avoid stopping scanner from within its own callback
  const [pendingScan, setPendingScan] = useState(null);

  // Process pending scan result outside the scanner callback
  useEffect(() => {
    if (!pendingScan) return;

    const processScan = async (decodedText) => {
      // Scanner is already paused from the callback — don't stop it, just process the result

      if (decodedText.startsWith("PRD:")) {
        const productId = parseInt(decodedText.substring(4));
        if (isNaN(productId)) {
          setErrorModal({ show: true, message: "Ongeldige product QR-code." });
          processingRef.current = false;
          return;
        }
        await fetchScanInfo(productId);
      } else if (decodedText.startsWith("LOC:")) {
        if (step === STEPS.LOCATION_SCAN || step === STEPS.PRODUCT) {
          const locationId = parseInt(decodedText.substring(4));
          if (isNaN(locationId)) {
            setErrorModal({ show: true, message: "Ongeldige locatie QR-code." });
            processingRef.current = false;
            startScanner();
            return;
          }
          await handleLocationScanned(locationId);
        } else {
          setErrorModal({
            show: true,
            message: "Dit is een locatie-QR. Scan een product-QR om te beginnen.",
          });
          processingRef.current = false;
          startScanner();
        }
      } else {
        setErrorModal({
          show: true,
          message: "Onbekende QR-code. Verwacht PRD:... of LOC:...",
        });
        processingRef.current = false;
        startScanner();
      }
    };

    const scanValue = pendingScan;
    setPendingScan(null);
    processScan(scanValue);
  }, [pendingScan]);

  // Fetch scan info for a product
  const fetchScanInfo = async (productId) => {
    setLoading(true);
    try {
      const data = await apiFetch(`/products/${productId}/scan-info`);
      setScanInfo(data);
      setPendingProductId(productId);

      if (data.locations.length === 1) {
        // Auto-select single location
        setSelectedLocationId(data.locations[0].location_id);
        setQuantity(data.locations[0].last_count_quantity ?? 0);
      } else if (data.locations.length === 0) {
        setSelectedLocationId(null);
        setQuantity(0);
      } else {
        // Multiple locations — no auto-select
        setSelectedLocationId(null);
        setQuantity(0);
      }
      setStep(STEPS.PRODUCT);
    } catch (err) {
      console.error("Scan info error:", err);
      setErrorModal({
        show: true,
        message: "Kon productinfo niet ophalen. Probeer opnieuw.",
      });
      startScanner();
    } finally {
      setLoading(false);
    }
  };

  // Handle location QR scanned
  const handleLocationScanned = async (locationId) => {
    setLoading(true);
    try {
      // Look up location by id
      const location = await apiFetch(`/locations/${locationId}`);

      if (!location) {
        setErrorModal({
          show: true,
          message: `Locatie met id ${locationId} niet gevonden.`,
        });
        startScanner();
        return;
      }

      // Assign product to location if not already assigned
      if (pendingProductId) {
        const alreadyAssigned = scanInfo?.locations?.some(
          (loc) => loc.location_id === location.id
        );
        if (!alreadyAssigned) {
          await apiFetch(`/locations/${location.id}/products/${pendingProductId}`, {
            method: "POST",
          });
        }

        // Update scan info with new location
        const updatedLocations = alreadyAssigned
          ? scanInfo.locations
          : [
              ...scanInfo.locations,
              {
                location_id: location.id,
                location_code: location.code,
                location_name: location.name,
                last_count_quantity: null,
                stock_count_name: null,
                stock_count_date: null,
              },
            ];

        setScanInfo((prev) => ({ ...prev, locations: updatedLocations }));
        setSelectedLocationId(location.id);

        // Find the last count for this location if it existed
        const existingLoc = scanInfo?.locations?.find(
          (loc) => loc.location_id === location.id
        );
        setQuantity(existingLoc?.last_count_quantity ?? 0);
        setStep(STEPS.PRODUCT);
      }
    } catch (err) {
      console.error("Location scan error:", err);
      setErrorModal({
        show: true,
        message: "Kon locatie niet verwerken. Probeer opnieuw.",
      });
      startScanner();
    } finally {
      setLoading(false);
    }
  };

  // Select a location from the radio list
  const handleSelectLocation = (locId) => {
    setSelectedLocationId(locId);
    const loc = scanInfo?.locations?.find((l) => l.location_id === locId);
    setQuantity(loc?.last_count_quantity ?? 0);
  };

  // Confirm current product count
  const handleConfirm = () => {
    if (!scanInfo || selectedLocationId === null) return;

    const selectedLoc = scanInfo.locations.find(
      (loc) => loc.location_id === selectedLocationId
    );

    const newItem = {
      product_id: scanInfo.id,
      product_name: scanInfo.product_name,
      brand: scanInfo.brand,
      location_id: selectedLocationId,
      location_code: selectedLoc?.location_code || "",
      location_name: selectedLoc?.location_name || "",
      quantity: quantity,
      unit_price: scanInfo.purchase_price_incl_discount,
    };

    // Check for duplicates (same product + location)
    const existingIdx = items.findIndex(
      (item) =>
        item.product_id === newItem.product_id &&
        item.location_id === newItem.location_id
    );

    if (existingIdx >= 0) {
      setDuplicateModal({ newItem, existingIdx });
      return;
    }

    addItem(newItem);
  };

  const addItem = (item) => {
    setItems((prev) => [item, ...prev]);
    resetProductState();
    setStep(STEPS.SCANNING);
    // Scanner will start via useEffect
  };

  const handleDuplicateReplace = () => {
    if (!duplicateModal) return;
    const { newItem, existingIdx } = duplicateModal;
    setItems((prev) => {
      const updated = [...prev];
      updated[existingIdx] = newItem;
      return updated;
    });
    setDuplicateModal(null);
    resetProductState();
    setStep(STEPS.SCANNING);
  };

  const handleDuplicateAdd = () => {
    if (!duplicateModal) return;
    const { newItem, existingIdx } = duplicateModal;
    setItems((prev) => {
      const updated = [...prev];
      updated[existingIdx] = {
        ...updated[existingIdx],
        quantity: updated[existingIdx].quantity + newItem.quantity,
      };
      return updated;
    });
    setDuplicateModal(null);
    resetProductState();
    setStep(STEPS.SCANNING);
  };

  const resetProductState = () => {
    setScanInfo(null);
    setSelectedLocationId(null);
    setQuantity(0);
    setPendingProductId(null);
  };

  // Remove item from list
  const handleRemoveItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // Start scanning (from setup or after product confirmation)
  useEffect(() => {
    if (step === STEPS.SCANNING && !scannerActive) {
      const timer = setTimeout(() => startScanner(), 300);
      return () => clearTimeout(timer);
    }
    if (step === STEPS.LOCATION_SCAN && !scannerActive) {
      const timer = setTimeout(() => startScanner(), 300);
      return () => clearTimeout(timer);
    }
  }, [step, scannerActive, startScanner]);

  // Pause scanner when leaving scanning steps (keep camera alive for quick resume)
  useEffect(() => {
    if (step !== STEPS.SCANNING && step !== STEPS.LOCATION_SCAN && scannerActive) {
      pauseScanner();
    }
  }, [step, scannerActive, pauseScanner]);

  // Save stock count
  const handleSave = async () => {
    if (items.length === 0) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        date: formData.date,
        notes: "Via QR-scanning",
        items: items.map((item) => ({
          product_id: item.product_id,
          location_id: item.location_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          notes: null,
        })),
      };

      await apiFetch("/stock-counts", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      navigate("/stock-counts");
    } catch (err) {
      console.error("Save error:", err);
      setErrorModal({
        show: true,
        message: "Kon de telling niet opslaan. Probeer opnieuw.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Non-mobile warning
  if (!isMobile) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center">
        <svg
          className="w-16 h-16 mx-auto mb-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          QR Scanning
        </h1>
        <p className="text-gray-600">
          Deze functie is enkel beschikbaar op een smartphone. Open de app op
          je telefoon om QR-codes te scannen.
        </p>
        <button
          onClick={() => navigate("/stock-counts")}
          className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Terug naar overzicht
        </button>
      </div>
    );
  }

  // Whether the scanner div should be visible
  const scannerVisible = step === STEPS.SCANNING || step === STEPS.LOCATION_SCAN;

  return (
    <div className="p-4 max-w-lg mx-auto">
      {/* Persistent QR scanner div — always in DOM, hidden when not scanning.
          This prevents the AbortError caused by React removing the video element. */}
      <div
        id="qr-reader"
        ref={scannerRef}
        className={`w-full rounded-lg overflow-hidden bg-black ${scannerVisible ? 'mb-4' : ''}`}
        style={{ minHeight: scannerVisible ? '280px' : '0', display: scannerVisible ? 'block' : 'none' }}
      />

      {/* STEP 1: SETUP */}
      {step === STEPS.SETUP && (
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-4">
            QR Voorraadtelling
          </h1>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Naam
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border rounded text-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Datum
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className="w-full px-3 py-2 border rounded text-lg"
                required
              />
            </div>
            <button
              onClick={() => {
                if (!formData.name.trim()) {
                  setErrorModal({
                    show: true,
                    message: "Geef een naam op voor de telling.",
                  });
                  return;
                }
                setStep(STEPS.SCANNING);
              }}
              className="w-full py-3 bg-blue-600 text-white rounded-lg text-lg font-medium hover:bg-blue-700 min-h-[48px]"
            >
              Start scannen
            </button>
            <button
              onClick={() => navigate("/stock-counts")}
              className="w-full py-2 text-gray-600 hover:text-gray-800"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: SCANNING */}
      {step === STEPS.SCANNING && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-bold text-gray-900">
              Scan product
            </h1>
            <span className="text-sm text-gray-500">
              {items.length} item{items.length !== 1 ? "s" : ""}
            </span>
          </div>

          {loading && (
            <div className="text-center py-4 text-gray-600">
              Product ophalen...
            </div>
          )}

          {/* Finish button */}
          {items.length > 0 && (
            <button
              onClick={() => setStep(STEPS.FINISH)}
              className="w-full py-3 bg-green-600 text-white rounded-lg text-lg font-medium hover:bg-green-700 mb-4 min-h-[48px]"
            >
              Telling afsluiten ({items.length} items)
            </button>
          )}

          {/* Scanned items list */}
          {items.length > 0 && (
            <div className="border rounded-lg divide-y">
              {items.map((item, idx) => (
                <div
                  key={`${item.product_id}-${item.location_id}-${idx}`}
                  className="px-3 py-2 flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.product_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.location_code} — {item.quantity}x
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveItem(idx)}
                    className="ml-2 p-1 text-red-400 hover:text-red-600"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 3: PRODUCT DETAIL */}
      {step === STEPS.PRODUCT && scanInfo && (
        <div>
          <h1 className="text-lg font-bold text-gray-900 mb-1">
            {scanInfo.product_name}
          </h1>
          <p className="text-sm text-gray-500 mb-4">{scanInfo.brand}</p>

          {scanInfo.selling_price_incl_vat && (
            <p className="text-sm text-gray-600 mb-4">
              Verkoopprijs: EUR {formatNumber(scanInfo.selling_price_incl_vat)}
            </p>
          )}

          {/* Location selection */}
          {scanInfo.locations.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800 font-medium mb-2">
                Dit product heeft nog geen locatie.
              </p>
              <button
                onClick={() => setStep(STEPS.LOCATION_SCAN)}
                className="w-full py-2 bg-yellow-600 text-white rounded text-sm font-medium hover:bg-yellow-700 min-h-[48px]"
              >
                Scan locatie QR-code
              </button>
            </div>
          )}

          {scanInfo.locations.length > 1 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Selecteer locatie:
              </p>
              <div className="space-y-2">
                {scanInfo.locations.map((loc) => (
                  <label
                    key={loc.location_id}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                      selectedLocationId === loc.location_id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="location"
                      checked={selectedLocationId === loc.location_id}
                      onChange={() => handleSelectLocation(loc.location_id)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{loc.location_name}</p>
                      <p className="text-xs text-gray-500">{loc.location_code}</p>
                    </div>
                    {loc.last_count_quantity != null && (
                      <span className="text-sm text-gray-500">
                        Laatste: {loc.last_count_quantity}
                      </span>
                    )}
                  </label>
                ))}
                {/* Option to scan location QR */}
                <button
                  onClick={() => setStep(STEPS.LOCATION_SCAN)}
                  className="w-full p-3 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 min-h-[48px]"
                >
                  Scan locatie QR-code
                </button>
              </div>
            </div>
          )}

          {/* Single location info */}
          {scanInfo.locations.length === 1 && (
            <div className="mb-4">
              <div className="bg-gray-50 rounded-lg p-3 mb-2">
                <p className="text-sm text-gray-600">
                  Locatie:{" "}
                  <span className="font-medium">
                    {scanInfo.locations[0].location_name}
                  </span>{" "}
                  <span className="text-gray-400">
                    ({scanInfo.locations[0].location_code})
                  </span>
                </p>
                {scanInfo.locations[0].last_count_quantity != null && (
                  <p className="text-xs text-gray-500 mt-1">
                    Laatste telling: {scanInfo.locations[0].last_count_quantity}
                  </p>
                )}
              </div>
              <button
                onClick={() => setStep(STEPS.LOCATION_SCAN)}
                className="w-full p-3 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 min-h-[48px]"
              >
                Scan andere locatie
              </button>
            </div>
          )}

          {/* Quantity input */}
          {selectedLocationId !== null && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">Aantal:</p>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setQuantity((q) => Math.max(0, q - 1))}
                  className="w-14 h-14 rounded-full bg-red-100 text-red-700 text-2xl font-bold flex items-center justify-center hover:bg-red-200 active:bg-red-300"
                >
                  −
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(0, parseInt(e.target.value) || 0))
                  }
                  className="w-24 text-center text-3xl font-bold border-b-2 border-gray-300 focus:border-blue-500 outline-none py-2"
                  min="0"
                />
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-14 h-14 rounded-full bg-green-100 text-green-700 text-2xl font-bold flex items-center justify-center hover:bg-green-200 active:bg-green-300"
                >
                  +
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-2">
            <button
              onClick={handleConfirm}
              disabled={selectedLocationId === null}
              className="w-full py-3 bg-blue-600 text-white rounded-lg text-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 min-h-[48px]"
            >
              Bevestigen
            </button>
            <button
              onClick={() => {
                resetProductState();
                setStep(STEPS.SCANNING);
              }}
              className="w-full py-2 text-gray-600 hover:text-gray-800"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: LOCATION SCAN */}
      {step === STEPS.LOCATION_SCAN && (
        <div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">
            Scan locatie QR-code
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            Scan de QR-code op het schap om de locatie te selecteren.
          </p>

          {loading && (
            <div className="text-center py-4 text-gray-600">
              Locatie verwerken...
            </div>
          )}

          <button
            onClick={() => setStep(STEPS.PRODUCT)}
            className="w-full py-2 text-gray-600 hover:text-gray-800"
          >
            Annuleren
          </button>
        </div>
      )}

      {/* STEP 5: FINISH */}
      {step === STEPS.FINISH && (
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-4">
            Telling afsluiten
          </h1>

          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Naam:</span> {formData.name}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Datum:</span> {formData.date}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Aantal items:</span> {items.length}
            </p>
          </div>

          {/* Items grouped by location */}
          <div className="border rounded-lg divide-y mb-4">
            {items.map((item, idx) => (
              <div
                key={`${item.product_id}-${item.location_id}-${idx}`}
                className="px-3 py-2 flex items-center justify-between"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.product_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.location_name} ({item.location_code})
                  </p>
                </div>
                <span className="text-sm font-bold text-gray-900 ml-2">
                  {item.quantity}x
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 bg-green-600 text-white rounded-lg text-lg font-medium hover:bg-green-700 disabled:bg-gray-400 min-h-[48px]"
            >
              {saving ? "Opslaan..." : "Opslaan"}
            </button>
            <button
              onClick={() => setStep(STEPS.SCANNING)}
              className="w-full py-2 text-gray-600 hover:text-gray-800"
            >
              Verder scannen
            </button>
            <button
              onClick={() => navigate("/stock-counts")}
              className="w-full py-2 text-red-600 hover:text-red-800"
            >
              Annuleren (niet opslaan)
            </button>
          </div>
        </div>
      )}

      {/* Duplicate Modal */}
      {duplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-2">Product al geteld</h2>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{duplicateModal.newItem.product_name}</strong> op{" "}
              <strong>{duplicateModal.newItem.location_code}</strong> staat al in
              de lijst. Huidig:{" "}
              {items[duplicateModal.existingIdx]?.quantity}, nieuw:{" "}
              {duplicateModal.newItem.quantity}.
            </p>
            <div className="space-y-2">
              <button
                onClick={handleDuplicateReplace}
                className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 min-h-[48px]"
              >
                Vervangen ({duplicateModal.newItem.quantity})
              </button>
              <button
                onClick={handleDuplicateAdd}
                className="w-full py-2 bg-green-600 text-white rounded hover:bg-green-700 min-h-[48px]"
              >
                Optellen (
                {items[duplicateModal.existingIdx]?.quantity +
                  duplicateModal.newItem.quantity}
                )
              </button>
              <button
                onClick={() => setDuplicateModal(null)}
                className="w-full py-2 border rounded hover:bg-gray-50 min-h-[48px]"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => {
          setErrorModal({ show: false, message: "" });
          // Restart scanner if we're in a scanning step
          if (step === STEPS.SCANNING || step === STEPS.LOCATION_SCAN) {
            startScanner();
          }
        }}
      />
    </div>
  );
}
